/**
 * LeadsTab — the מתעניינים tab of ארגז הכלים (Leads v2, leads_plan.md §6).
 *
 * Clinic-wide pipeline: search, KPI strip, filter chips, list | kanban views,
 * create/edit/detail modals, status manager, soft delete + archive, assign,
 * lost reason, follow-ups. Access is gated by `leads.access` (the page hides
 * the tab); every write goes through leadsApi → firebase.js v2 helpers.
 *
 * Deep links handled by the parent: { action: "new" } | { leadId }.
 *
 * Error codes: CK-4010–4014 (legacy), CK-4015–4019 (v2 actions).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass,
  X,
  Plus,
  Tag,
  ListDashes,
  Kanban,
  UserPlus,
  Clock,
  TrendUp,
  Hourglass,
  Info,
  Archive,
  Trash,
  LockSimple,
} from "@phosphor-icons/react";
import { useToast } from "../../contexts/ToastContext";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectTherapistProfile,
  selectClinicProfile,
  selectIsAdmin,
  selectIsSecretary,
  selectAssignableTherapists,
  setClinicProfile,
} from "../../store/slices/authSlice";
import { useGetClinicTherapistsQuery } from "../../store/services/therapistApi";
import {
  useGetLeadsQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useDeleteLeadMutation,
  useAddLeadDevelopmentLogMutation,
  useConvertLeadToPatientMutation,
  useRestoreLeadMutation,
  useHardDeleteLeadMutation,
  useSetLeadLostMutation,
  useMarkLeadContactedMutation,
  useUpdateClinicLeadStatusesMutation,
} from "../../store/services/leadsApi";
import { DEFAULT_POLLING_INTERVAL, useVisibilityRefresh } from "../../hooks/useRealtimeQuery";
import { resolveLeadStatuses, clinicHasLeadStatuses, findLeadStatus } from "../../firebase";
import { persistCustomReferralSource } from "../../utils/referralSources";
import { LEAD_INPUT_ERROR_LABELS } from "../../utils/leadContract";
import ContentLoader from "../shared/ContentLoader";
import ConfirmDialog from "../ui/ConfirmDialog";
import EmptyDraw from "../ui/EmptyDraw";
import HoverTip from "../ui/HoverTip";
import CustomClinikitActionButton from "../ui/CustomClinikitActionButton";
import { HelpButton } from "../shared";
import LeadCard from "./LeadCard";
import LeadsKanban from "./LeadsKanban";
import LeadFormModal, { EMPTY_LEAD_FORM, leadToForm, formToPayload } from "./LeadFormModal";
import LeadDetailModal from "./LeadDetailModal";
import LeadStatusManagerModal from "./LeadStatusManagerModal";
import LeadLostModal from "./LeadLostModal";
import LeadAssignModal from "./LeadAssignModal"; // secretary-only "pick a treating therapist" step on convert
import {
  LEADS_VIEW_STORAGE_KEY,
  statusStyleVars,
  isOpenLead,
  isArchivedStatus,
  getFollowUpState,
  buildDuplicateMap,
  leadIdentityKey,
  leadSourceFilterKey,
  leadSourceFilterLabel,
  leadMatchesQuery,
  computeLeadKpis,
  sortLeads,
} from "./leadsUtils";
import "./LeadsTab.css";

const FOLLOW_FILTERS = [
  { id: "all", label: "הכל" },
  { id: "today", label: "היום" },
  { id: "overdue", label: "באיחור" },
  { id: "none", label: "ללא מעקב" },
];

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 768 : false));
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setMobile(e.matches);
    setMobile(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return mobile;
};

function LeadsTab({ hasAccess = true, deepLink, onDeepLinkConsumed }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const isMobile = useIsMobile();

  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const isSecretary = useSelector(selectIsSecretary);
  const assignableTherapists = useSelector(selectAssignableTherapists);
  // Only a secretary needs the therapist list (to pick the treating therapist when converting)
  useGetClinicTherapistsQuery({ clinicId }, { skip: !clinicId || !isSecretary });

  // ---------- data ----------
  const { data: leads = [], isLoading, refetch } = useGetLeadsQuery(
    { clinicId },
    { skip: !clinicId || !hasAccess, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );
  useVisibilityRefresh(refetch);

  const [showDeleted, setShowDeleted] = useState(false);
  const { data: leadsWithDeleted = [] } = useGetLeadsQuery(
    { clinicId, includeDeleted: true },
    { skip: !clinicId || !isAdmin || !showDeleted || !hasAccess }
  );

  const statuses = useMemo(() => resolveLeadStatuses(clinicProfile, therapistProfile), [clinicProfile, therapistProfile]);
  const statusOf = useCallback((lead) => findLeadStatus(statuses, lead?.status), [statuses]);
  const isAssignable = useCallback((id) => !!id && assignableTherapists.some((t) => t.uid === id), [assignableTherapists]);

  // ---------- mutations ----------
  const [createLead, { isLoading: isCreating }] = useCreateLeadMutation();
  const [updateLead, { isLoading: isUpdating }] = useUpdateLeadMutation();
  const [deleteLead] = useDeleteLeadMutation();
  const [addLeadDevelopmentLog, { isLoading: isAddingLog }] = useAddLeadDevelopmentLogMutation();
  const [convertLeadToPatient, { isLoading: isConverting }] = useConvertLeadToPatientMutation();
  const [restoreLead] = useRestoreLeadMutation();
  const [hardDeleteLead] = useHardDeleteLeadMutation();
  const [setLeadLost, { isLoading: isClosing }] = useSetLeadLostMutation();
  const [markLeadContacted, { isLoading: isMarking }] = useMarkLeadContactedMutation();
  const [updateClinicLeadStatuses, { isLoading: isSavingStatuses }] = useUpdateClinicLeadStatusesMutation();
  const busy = isConverting || isClosing || isMarking;

  // ---------- view / filters ----------
  const [view, setView] = useState(() => {
    const saved = localStorage.getItem(LEADS_VIEW_STORAGE_KEY);
    return saved === "kanban" || saved === "list" ? saved : "list";
  });
  const effectiveView = isMobile ? "list" : view;
  const changeView = (v) => {
    setView(v);
    localStorage.setItem(LEADS_VIEW_STORAGE_KEY, v);
  };
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [followFilter, setFollowFilter] = useState("all");
  const [archiveMode, setArchiveMode] = useState(false);

  // ---------- modals ----------
  const [formState, setFormState] = useState(null); // { mode, lead|null, initialForm }
  const [viewingId, setViewingId] = useState(null);
  const [statusManagerOpen, setStatusManagerOpen] = useState(false);
  const [lostTarget, setLostTarget] = useState(null); // { lead }
  const [convertPick, setConvertPick] = useState(null); // lead (secretary must pick a treating therapist)
  const [confirm, setConfirm] = useState({ isOpen: false });
  const closeConfirm = () => setConfirm((c) => ({ ...c, isOpen: false }));

  const viewingLead = useMemo(() => {
    if (!viewingId) return null;
    return leads.find((l) => l.id === viewingId) || leadsWithDeleted.find((l) => l.id === viewingId) || null;
  }, [viewingId, leads, leadsWithDeleted]);

  // Deep links from the page (?tab=leads&action=new / &lead=<id>)
  useEffect(() => {
    if (!deepLink) return;
    if (deepLink.action === "new") {
      setFormState({ mode: "create", lead: null, initialForm: EMPTY_LEAD_FORM });
    } else if (deepLink.leadId) {
      setViewingId(deepLink.leadId);
    }
    onDeepLinkConsumed?.();
  }, [deepLink, onDeepLinkConsumed]);

  // ---------- derived ----------
  const duplicateMap = useMemo(() => buildDuplicateMap(leads), [leads]);
  const kpis = useMemo(() => computeLeadKpis(leads), [leads]);
  const sourceOptions = useMemo(() => {
    const set = new Map();
    leads.forEach((l) => {
      const k = leadSourceFilterKey(l);
      set.set(k, (set.get(k) || 0) + 1);
    });
    return [...set.entries()].sort((a, b) => b[1] - a[1]);
  }, [leads]);

  const baseList = useMemo(() => {
    if (showDeleted && isAdmin) return leadsWithDeleted.filter((l) => l.isDeleted === true);
    return leads.filter((l) => (archiveMode ? isArchivedStatus(l.status) || !!l.convertedToPatientId : isOpenLead(l)));
  }, [leads, leadsWithDeleted, archiveMode, showDeleted, isAdmin]);

  const filtered = useMemo(() => {
    let list = baseList;
    if (statusFilter !== "all") list = list.filter((l) => l.status === statusFilter);
    if (sourceFilter !== "all") list = list.filter((l) => leadSourceFilterKey(l) === sourceFilter);
    if (followFilter !== "all") {
      list = list.filter((l) => {
        const st = getFollowUpState(l);
        if (followFilter === "none") return !l.nextFollowUpAt;
        return st === followFilter;
      });
    }
    if (query.trim()) list = list.filter((l) => leadMatchesQuery(l, query));
    return sortLeads(list, statuses);
  }, [baseList, statusFilter, sourceFilter, followFilter, query, statuses]);

  const statusCounts = useMemo(() => {
    const counts = { all: baseList.length };
    baseList.forEach((l) => {
      counts[l.status] = (counts[l.status] || 0) + 1;
    });
    return counts;
  }, [baseList]);
  const usageCounts = useMemo(() => {
    const c = {};
    leads.filter(isOpenLead).forEach((l) => {
      c[l.status] = (c[l.status] || 0) + 1;
    });
    return c;
  }, [leads]);

  const visibleStatuses = useMemo(
    () => statuses.filter((s) => (archiveMode ? isArchivedStatus(s.value) : !isArchivedStatus(s.value))),
    [statuses, archiveMode]
  );
  const kanbanStatuses = useMemo(() => statuses, [statuses]);
  const kanbanLeads = useMemo(() => {
    // Kanban always shows open leads; archive columns are collapsed inside the board.
    let list = leads.filter((l) => l.isDeleted !== true);
    if (sourceFilter !== "all") list = list.filter((l) => leadSourceFilterKey(l) === sourceFilter);
    if (query.trim()) list = list.filter((l) => leadMatchesQuery(l, query));
    return list;
  }, [leads, sourceFilter, query]);

  // ---------- handlers ----------
  const openCreate = () => setFormState({ mode: "create", lead: null, initialForm: EMPTY_LEAD_FORM });
  const openEdit = (lead) => setFormState({ mode: "edit", lead, initialForm: leadToForm(lead) });
  const closeForm = () => setFormState(null);

  const handleSaveForm = async (form) => {
    const { leadData } = formToPayload(form);
    // Leads are clinic leads: no assignment. `therapistId` is only legacy
    // "created by" data (actor); a secretary never owns a lead ⇒ null.
    const owner = isSecretary ? { therapistId: null, therapistName: null } : null;
    try {
      if (formState?.mode === "edit") {
        await updateLead({ clinicId, leadId: formState.lead.id, leadData, therapistId, therapistName, owner }).unwrap();
      } else {
        await createLead({ clinicId, leadData, therapistId, therapistName, owner }).unwrap();
      }
      if (leadData.referralSource) {
        persistCustomReferralSource({ value: leadData.referralSource, therapistId, therapistProfile, dispatch });
      }
      showSuccess(formState?.mode === "edit" ? "המתעניין עודכן בהצלחה" : "המתעניין נוצר בהצלחה");
      closeForm();
    } catch (error) {
      console.error("[CK-4010] Error saving lead:", {
        errorCode: "CK-4010",
        errorMessage: error.message,
        clinicId,
        leadId: formState?.lead?.id,
        isEditing: formState?.mode === "edit",
        timestamp: new Date().toISOString(),
      });
      showError(LEAD_INPUT_ERROR_LABELS[error.message] || "שגיאה בשמירת המתעניין");
    }
  };

  const handleStatusChange = async (lead, newStatus) => {
    if (!newStatus || newStatus === lead.status) return;
    if (newStatus === "lost") {
      setLostTarget({ lead });
      return;
    }
    try {
      await updateLead({ clinicId, leadId: lead.id, leadData: { status: newStatus }, therapistId, therapistName }).unwrap();
    } catch (error) {
      console.error("[CK-4012] Error updating lead status:", {
        errorCode: "CK-4012",
        errorMessage: error.message,
        clinicId,
        leadId: lead.id,
        newStatus,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון הסטטוס");
    }
  };

  const handleConfirmLost = async ({ lostReason, lostNote }) => {
    const lead = lostTarget?.lead;
    if (!lead) return;
    try {
      await setLeadLost({ clinicId, leadId: lead.id, lostReason, lostNote, therapistId, therapistName, leadName: lead.name }).unwrap();
      setLostTarget(null);
      showSuccess("המתעניין הועבר לארכיון");
    } catch (error) {
      console.error("[CK-4016] Error closing lead as lost:", {
        errorCode: "CK-4016",
        errorMessage: error.message,
        clinicId,
        leadId: lead.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בסגירת המתעניין");
    }
  };

  const handleDelete = (lead) => {
    setConfirm({
      isOpen: true,
      type: "danger",
      icon: "delete",
      title: "מחיקת מתעניין",
      message: `למחוק את "${lead.name}"? המתעניין יועבר לארכיון המחוקים וניתן לשחזר אותו.`,
      confirmText: "מחק",
      onConfirm: async () => {
        try {
          await deleteLead({ clinicId, leadId: lead.id, therapistId, therapistName, leadName: lead.name }).unwrap();
          if (viewingId === lead.id) setViewingId(null);
        } catch (error) {
          console.error("[CK-4011] Error deleting lead:", {
            errorCode: "CK-4011",
            errorMessage: error.message,
            clinicId,
            leadId: lead.id,
            timestamp: new Date().toISOString(),
          });
          showError("שגיאה במחיקת המתעניין");
        }
      },
    });
  };

  const handleRestore = async (lead) => {
    try {
      await restoreLead({ clinicId, leadId: lead.id, therapistId, therapistName, leadName: lead.name }).unwrap();
      showSuccess("המתעניין שוחזר");
    } catch (error) {
      console.error("[CK-4018] Error restoring lead:", {
        errorCode: "CK-4018",
        errorMessage: error.message,
        clinicId,
        leadId: lead.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשחזור המתעניין");
    }
  };

  const handleHardDelete = (lead) => {
    setConfirm({
      isOpen: true,
      type: "danger",
      icon: "delete",
      title: "מחיקה לצמיתות",
      message: `למחוק את "${lead.name}" לצמיתות? לא ניתן לשחזר פעולה זו.`,
      confirmText: "מחק לצמיתות",
      onConfirm: async () => {
        try {
          await hardDeleteLead({ clinicId, leadId: lead.id, therapistId, therapistName, leadName: lead.name }).unwrap();
        } catch (error) {
          console.error("[CK-4018] Error hard-deleting lead:", {
            errorCode: "CK-4018",
            errorMessage: error.message,
            clinicId,
            leadId: lead.id,
            timestamp: new Date().toISOString(),
          });
          showError("שגיאה במחיקת המתעניין");
        }
      },
    });
  };

  const handleAddLog = async (content) => {
    if (!viewingLead) return;
    try {
      await addLeadDevelopmentLog({ clinicId, leadId: viewingLead.id, content, authorId: therapistId, authorName: therapistName }).unwrap();
    } catch (error) {
      console.error("[CK-4013] Error adding development log:", {
        errorCode: "CK-4013",
        errorMessage: error.message,
        clinicId,
        leadId: viewingLead.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהוספת התיעוד");
    }
  };

  const handleMarkContacted = async (nextFollowUpAt) => {
    if (!viewingLead) return;
    try {
      await markLeadContacted({ clinicId, leadId: viewingLead.id, nextFollowUpAt, therapistId, therapistName, leadName: viewingLead.name }).unwrap();
      showSuccess("נרשם שנוצר קשר");
    } catch (error) {
      console.error("[CK-4017] Error marking lead contacted:", {
        errorCode: "CK-4017",
        errorMessage: error.message,
        clinicId,
        leadId: viewingLead.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון המתעניין");
    }
  };

  /**
   * Convert → patient (leads_plan.md §4.3, simplified): the patient belongs to
   * the acting therapist; a secretary (who can never own a patient) picks the
   * treating therapist first.
   */
  const runConvert = (lead, ownerTherapistId) => {
    setConfirm({
      isOpen: true,
      type: "success",
      icon: "check",
      title: "המרה למטופל",
      message: `להמיר את "${lead.name}" למטופל במערכת? המתעניין יסומן כ"הפך למטופל".`,
      confirmText: "המר למטופל",
      onConfirm: async () => {
        try {
          const result = await convertLeadToPatient({ clinicId, leadId: lead.id, therapistId, therapistName, ownerTherapistId }).unwrap();
          setViewingId(null);
          navigate(`/dashboard/patients/${result.patientId}`);
        } catch (error) {
          console.error("[CK-4014] Error converting lead to patient:", {
            errorCode: "CK-4014",
            errorMessage: error.message,
            clinicId,
            leadId: lead.id,
            timestamp: new Date().toISOString(),
          });
          showError("שגיאה בהמרת המתעניין למטופל. נסה שוב.");
        }
      },
    });
  };
  const handleConvert = (lead) => {
    if (isSecretary) {
      // Legacy secretary-created leads may carry an assignable therapistId — reuse it, else ask.
      if (isAssignable(lead.therapistId)) runConvert(lead, lead.therapistId);
      else setConvertPick(lead);
      return;
    }
    runConvert(lead, therapistId);
  };

  const handleSaveStatuses = async (list) => {
    try {
      const saved = await updateClinicLeadStatuses({ clinicId, statuses: list, therapistId, therapistName }).unwrap();
      dispatch(setClinicProfile({ ...(clinicProfile || {}), leadStatuses: saved }));
      setStatusManagerOpen(false);
      showSuccess("סטטוסי המתעניינים נשמרו");
    } catch (error) {
      console.error("[CK-4019] Error saving clinic lead statuses:", {
        errorCode: "CK-4019",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הסטטוסים");
    }
  };

  const handleKanbanMove = (lead, statusValue) => handleStatusChange(lead, statusValue);

  // ---------- render ----------
  const isEmptyClinic = !isLoading && leads.filter((l) => l.isDeleted !== true).length === 0;

  if (!hasAccess) {
    return (
      <div className="ld-tab">
        <div className="ld-empty ld-locked">
          <span className="ld-locked-icon"><LockSimple size={28} weight="fill" /></span>
          <h3>המתעניינים מנוהלים על ידי מנהלי הקליניקה</h3>
          <p>בקליניקה זו רק מנהלים רואים ועורכים את רשימת המתעניינים. מנהל/ת יכול/ה לפתוח את הגישה לכל הצוות בהגדרות הקליניקה ← יומן ← "מטפלים רואים ועורכים מתעניינים".</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ld-tab">
      {/* Header row */}
      <div className="ld-header">
        <div className="ld-search">
          <MagnifyingGlass size={16} weight="regular" />
          <input
            type="text"
            placeholder="חיפוש לפי שם, טלפון או אימייל..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="חיפוש מתעניינים"
          />
          {query && (
            <button className="ld-search-clear" onClick={() => setQuery("")} aria-label="נקה חיפוש">
              <X size={13} weight="bold" />
            </button>
          )}
        </div>
        <div className="ld-header-actions">
          {!isMobile && (
            <div className="ld-view-tabs" role="tablist" aria-label="תצוגה">
              <button type="button" role="tab" aria-selected={effectiveView === "list"} className={`ld-view-tab${effectiveView === "list" ? " active" : ""}`} onClick={() => changeView("list")}>
                <ListDashes size={15} weight={effectiveView === "list" ? "fill" : "regular"} />
                <span>רשימה</span>
              </button>
              <button type="button" role="tab" aria-selected={effectiveView === "kanban"} className={`ld-view-tab${effectiveView === "kanban" ? " active" : ""}`} onClick={() => changeView("kanban")}>
                <Kanban size={15} weight={effectiveView === "kanban" ? "fill" : "regular"} />
                <span>לוח</span>
              </button>
            </div>
          )}
          <HelpButton featureKey="leads_tab" />
          <button className="ld-secondary-btn manage-statuses-btn" onClick={() => setStatusManagerOpen(true)} title="ניהול סטטוסים">
            <Tag size={14} weight="bold" />
            <span>סטטוסים</span>
          </button>
          <CustomClinikitActionButton icon={<Plus size={16} weight="bold" />} onClick={openCreate}>
            מתעניין חדש
          </CustomClinikitActionButton>
        </div>
      </div>

      {/* KPI strip */}
      <div className="ld-kpis">
        <div className="ld-kpi">
          <span className="ld-kpi-icon"><UserPlus size={16} weight="fill" /></span>
          <div className="ld-kpi-text">
            <span className="ld-kpi-value">{kpis.newThisWeek}</span>
            <span className="ld-kpi-label">חדשים השבוע</span>
          </div>
        </div>
        <div className={`ld-kpi${kpis.overdue > 0 ? " is-alert" : ""}`}>
          <span className="ld-kpi-icon"><Clock size={16} weight="fill" /></span>
          <div className="ld-kpi-text">
            <span className="ld-kpi-value">
              {kpis.dueToday}
              {kpis.overdue > 0 && <span className="ld-kpi-sub">+{kpis.overdue} באיחור</span>}
            </span>
            <span className="ld-kpi-label">לטיפול היום</span>
          </div>
        </div>
        <div className="ld-kpi">
          <span className="ld-kpi-icon"><Hourglass size={16} weight="fill" /></span>
          <div className="ld-kpi-text">
            <span className="ld-kpi-value">{kpis.inProgress}</span>
            <span className="ld-kpi-label">בתהליך</span>
          </div>
        </div>
        <div className="ld-kpi">
          <span className="ld-kpi-icon"><TrendUp size={16} weight="fill" /></span>
          <div className="ld-kpi-text">
            <span className="ld-kpi-value">{kpis.conversionRate == null ? "—" : `${kpis.conversionRate}%`}</span>
            <span className="ld-kpi-label">
              המרה · 90 ימים
              <HoverTip text="כמה מהמתעניינים שנוצרו ב-90 הימים האחרונים הפכו למטופלים.">
                <Info size={12} weight="regular" className="ld-kpi-info" tabIndex={0} />
              </HoverTip>
            </span>
          </div>
        </div>
      </div>

      {/* Filters (list view) */}
      {effectiveView === "list" && (
        <div className="ld-filters">
          <div className="ld-chips-row" role="group" aria-label="סטטוס">
            <button className={`ld-filter-chip ld-filter-all${statusFilter === "all" ? " active" : ""}`} onClick={() => setStatusFilter("all")}>
              הכל ({statusCounts.all})
            </button>
            {visibleStatuses.map((s) => (
              <button
                key={s.value}
                className={`ld-filter-chip${statusFilter === s.value ? " active" : ""}`}
                style={statusStyleVars(s)}
                onClick={() => setStatusFilter((v) => (v === s.value ? "all" : s.value))}
              >
                <span>{s.label}</span>
                <span className="ld-filter-count">({statusCounts[s.value] || 0})</span>
              </button>
            ))}
            <span className="ld-chips-spacer" />
            <button
              className={`ld-filter-chip ld-filter-archive${archiveMode ? " active" : ""}`}
              onClick={() => {
                setArchiveMode((v) => !v);
                setShowDeleted(false);
                setStatusFilter("all");
              }}
              title="הפכו למטופלים / לא רלוונטיים"
            >
              <Archive size={13} weight="bold" />
              <span>ארכיון</span>
            </button>
            {isAdmin && archiveMode && (
              <button className={`ld-filter-chip ld-filter-deleted${showDeleted ? " active" : ""}`} onClick={() => setShowDeleted((v) => !v)} title="מתעניינים שנמחקו">
                <Trash size={13} weight="bold" />
                <span>מחוקים</span>
              </button>
            )}
          </div>

          <div className="ld-chips-row ld-chips-secondary">
            {sourceOptions.length > 1 && (
              <div className="ld-chip-group" role="group" aria-label="מקור">
                <span className="ld-chip-group-label">מקור</span>
                <button className={`ld-filter-chip ld-filter-all${sourceFilter === "all" ? " active" : ""}`} onClick={() => setSourceFilter("all")}>הכל</button>
                {sourceOptions.map(([key, count]) => (
                  <button key={key} className={`ld-filter-chip ld-filter-all${sourceFilter === key ? " active" : ""}`} onClick={() => setSourceFilter((v) => (v === key ? "all" : key))}>
                    {leadSourceFilterLabel(key)} <span className="ld-filter-count">({count})</span>
                  </button>
                ))}
              </div>
            )}
            {!archiveMode && (
              <div className="ld-chip-group" role="group" aria-label="מעקב">
                <span className="ld-chip-group-label">מעקב</span>
                {FOLLOW_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    className={`ld-filter-chip ld-filter-all${followFilter === f.id ? " active" : ""}${f.id === "overdue" ? " ld-filter-overdue" : ""}${f.id === "today" ? " ld-filter-today" : ""}`}
                    onClick={() => setFollowFilter(f.id)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      {isLoading ? (
        <ContentLoader message="טוען מתעניינים..." />
      ) : isEmptyClinic ? (
        <div className="ld-empty">
          <EmptyDraw variant="lead" />
          <p>עקוב/י אחרי פניות ומתעניינים — פרטים, סטטוס, מעקבים ותיעוד עד שהם הופכים למטופלים. אפשר גם לקלוט מתעניינים אוטומטית מהאתר או מקמפיינים דרך הגדרות הקליניקה ← אינטגרציות.</p>
          <button className="ld-secondary-btn" onClick={openCreate}>
            <Plus size={16} weight="bold" />
            <span>הוסף מתעניין ראשון</span>
          </button>
        </div>
      ) : effectiveView === "kanban" ? (
        <LeadsKanban
          leads={kanbanLeads}
          statuses={kanbanStatuses}
          duplicateMap={duplicateMap}
          identityKeyOf={leadIdentityKey}
          onOpen={(lead) => setViewingId(lead.id)}
          onMove={handleKanbanMove}
          busy={busy}
        />
      ) : filtered.length === 0 ? (
        <div className="ld-empty ld-empty-filtered">
          <EmptyDraw variant="search" size="sm" />
          <p>{showDeleted ? "אין מתעניינים מחוקים" : archiveMode ? "הארכיון ריק" : "אין מתעניינים שתואמים לסינון"}</p>
        </div>
      ) : (
        <div className="ld-list">
          {filtered.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              status={statusOf(lead)}
              statuses={statuses}
              isDuplicate={(duplicateMap.get(leadIdentityKey(lead)) || 0) > 1}
              isDeletedView={showDeleted}
              isAdmin={isAdmin}
              busy={busy}
              onOpen={(l) => setViewingId(l.id)}
              onStatusChange={handleStatusChange}
              onConvert={handleConvert}
              onEdit={openEdit}
              onDelete={handleDelete}
              onRestore={handleRestore}
              onHardDelete={handleHardDelete}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <LeadFormModal
        isOpen={!!formState}
        mode={formState?.mode || "create"}
        initialForm={formState?.initialForm}
        statuses={statuses}
        therapistProfile={therapistProfile}
        isSaving={isCreating || isUpdating}
        onClose={closeForm}
        onSave={handleSaveForm}
      />

      {viewingLead && (
        <LeadDetailModal
          lead={viewingLead}
          status={statusOf(viewingLead)}
          statuses={statuses}
          isAddingLog={isAddingLog}
          isBusy={busy}
          onClose={() => setViewingId(null)}
          onStatusChange={handleStatusChange}
          onConvert={handleConvert}
          onEdit={(l) => {
            setViewingId(null);
            openEdit(l);
          }}
          onAddLog={handleAddLog}
          onMarkContacted={handleMarkContacted}
        />
      )}

      <LeadStatusManagerModal
        isOpen={statusManagerOpen}
        statuses={statuses}
        isImport={!clinicHasLeadStatuses(clinicProfile)}
        usageCounts={usageCounts}
        isSaving={isSavingStatuses}
        onClose={() => setStatusManagerOpen(false)}
        onSave={handleSaveStatuses}
      />

      <LeadLostModal lead={lostTarget?.lead || null} isSaving={isClosing} onClose={() => setLostTarget(null)} onConfirm={handleConfirmLost} />

      <LeadAssignModal
        lead={convertPick}
        therapists={assignableTherapists}
        currentTherapistId={therapistId}
        allowNone={false}
        title="המרה למטופל"
        hint="למתעניין אין מטפל/ת משויך/ת. יש לבחור מטפל/ת עבור המטופל החדש."
        confirmLabel="המשך"
        isSaving={isConverting}
        onClose={() => setConvertPick(null)}
        onConfirm={(pickedId) => {
          const lead = convertPick;
          setConvertPick(null);
          if (lead && pickedId) runConvert(lead, pickedId);
        }}
      />

      <ConfirmDialog
        isOpen={confirm.isOpen}
        onClose={closeConfirm}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        type={confirm.type}
        icon={confirm.icon}
        confirmText={confirm.confirmText || "אישור"}
      />
    </div>
  );
}

export default LeadsTab;
